import { useQuery } from '@tanstack/react-query';
import { useHub } from '@/contexts/HubContext';
import { platformApi } from '@/lib/platform-api';
import type { MoneyBucket } from '../utils/groupMoney';

export interface FinanceMetrics {
  collections: MoneyBucket[];
  paymentCount30d: number;
  pendingPayoutsCount: number;
  pendingPayouts: MoneyBucket[];
  reconNeedsReview: number;
}

type DashboardMetric = {
  collections?: MoneyBucket[];
  payment_count?: number;
  pending_payouts_count?: number;
  pending_payouts?: MoneyBucket[];
  recon_needs_review?: number;
};

async function fetchFinanceMetrics(clientId?: string | null): Promise<FinanceMetrics> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/metrics/dashboard', {
    params: { query: { client_id: clientId ?? undefined, period_days: 30 } },
  });
  if (error) throw new Error('Failed to fetch finance metrics');
  const metrics = (data as { data?: DashboardMetric } | undefined)?.data;
  return {
    collections: metrics?.collections ?? [],
    paymentCount30d: metrics?.payment_count ?? 0,
    pendingPayoutsCount: metrics?.pending_payouts_count ?? 0,
    pendingPayouts: metrics?.pending_payouts ?? [],
    reconNeedsReview: metrics?.recon_needs_review ?? 0,
  };
}

export function useFinanceMetrics(clientId?: string | null) {
  const { selectedOperator } = useHub();
  const operatorId = selectedOperator?.id;

  return useQuery<FinanceMetrics, Error>({
    queryKey: ['finance-metrics', operatorId, clientId ?? null],
    queryFn: () => {
      if (!operatorId) throw new Error('No operator selected');
      return fetchFinanceMetrics(clientId);
    },
    enabled: !!operatorId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Count of payouts pending review — for the Client Payouts nav badge.
 *
 * Reads `pending_payouts_count` from the bounded, ETag-cached
 * `/platform/v1/finance/metrics/dashboard` aggregation, which the Finance
 * Dashboard already fetches. Shares the exact query key + queryFn of
 * `useFinanceMetrics` with the same client filter so React Query dedupes the two into
 * a single request and the cache always holds a `FinanceMetrics` object; the
 * count is derived per-observer via `select`, avoiding any cache type
 * collision with the dashboard's full-metric consumer. This intentionally
 * does NOT touch the payouts-balance-breakdown metric — that is the Client
 * Payouts page waterfall and stays confined to its context page.
 *
 * Pinned to fetch-once: `staleTime: Infinity` plus no focus/reconnect refetch
 * means the badge shows the count from the last full context load and does
 * not keep re-calling the metric endpoint on every tab focus. A full context
 * reload (route remount) or an explicit query invalidation (force refresh,
 * e.g. after a payout mutation) is the only thing that re-fetches.
 */
export function usePendingPayoutsCount(enabled = true, clientId?: string | null) {
  const { selectedOperator } = useHub();
  const operatorId = selectedOperator?.id;

  return useQuery<FinanceMetrics, Error, number>({
    queryKey: ['finance-metrics', operatorId, clientId ?? null],
    queryFn: () => {
      if (!operatorId) throw new Error('No operator selected');
      return fetchFinanceMetrics(clientId);
    },
    enabled: enabled && !!operatorId,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: (data) => data.pendingPayoutsCount,
  });
}
